import { useSelector } from 'react-redux';
import { isAndroid, isDesktop, isIOS, isMobile, isWinPhone } from 'react-device-detect';

import { selectIsInAppWrapper, selectLocale } from '@/store/selectors/commonSelectors';
import { envIsCanada, envIsMexico, envIsNormal, envIsSweden } from '@/constants/constants';

export enum DevicePlatform {
  Android = 'android',
  iOS = 'ios',
  Huawei = 'huawei',

  WindowsPhone = 'windows-phone',
  Desktop = 'desktop',
}

function getDevicePlatform() {
  if (isMobile && isAndroid) return DevicePlatform.Android;
  if (isMobile && isIOS) return DevicePlatform.iOS;
  if (isMobile && isWinPhone) return DevicePlatform.WindowsPhone;
  if (isMobile && !isIOS && !isWinPhone && !isAndroid) return DevicePlatform.Huawei;
  return DevicePlatform.Desktop;
}

function mapToImageLocale(locale: string) {
  if (envIsCanada()) {
    return locale === 'ca' ? 'on' : 'on-fr';
  }
  switch (locale) {
    case 'ca-fr':
      return 'fr';
    case 'row':
    case 'ca':
    case 'za':
    case 'ae':
    case 'nz':
    case 'fi-en':
      return 'en';
    default:
      return locale;
  }
}

function mapToCmsPromotionLocale(locale: string) {
  if (envIsCanada()) {
    switch (locale) {
      case 'ca':
        return 'ca-on';
      case 'ca-fr':
        return 'ca-fr-on';
    }
  }
  return locale;
}

function getCanadaAndroidImage(locale: string): string {
  const imageLocale = locale === 'ca-fr' ? 'FR' : 'EN';
  return `/app-download/Google_Play_Store_badge_${imageLocale}.png`;
}

interface MobileDownloadUrlProps {
  mobileAppPromotionUrl: string | null;
  image: string | null;
}

function createDesktopReturn(
  mappedCmsLocale: string,
  mappedImageLocale: string,
  platforms: DevicePlatform[],
) {
  return platforms.map(platform => ({
    mobileAppPromotionUrl: `/promotion/mobile-app-${mappedCmsLocale}`,
    image: `/app-download/${platform}-${mappedImageLocale}.png`,
  }));
}

const useGetMobileAppDownloadUrl = (): MobileDownloadUrlProps[] => {
  const locale = useSelector(selectLocale);
  const isInAppWrapper = useSelector(selectIsInAppWrapper);
  const isROC = envIsNormal() && ['ca', 'ca-fr'].includes(locale);

  // If user already in app, don't show
  if (isInAppWrapper) {
    return [];
  }

  const mappedCmsLocale = mapToCmsPromotionLocale(locale);
  const mappedImageLocale = mapToImageLocale(locale);

  // No Sweden supported
  if (envIsSweden()) {
    return [];
  }

  // Only Android and Huawei supported for Mexico
  if (isDesktop && envIsMexico()) {
    return createDesktopReturn(mappedCmsLocale, mappedImageLocale, [
      DevicePlatform.Android,
      DevicePlatform.Huawei,
    ]);
  }

  // For ENTIRE Canada only IOS and Android are supported
  if (isDesktop && envIsCanada()) {
    return [
      {
        mobileAppPromotionUrl: `/promotion/mobile-app-${mappedCmsLocale}`,
        image: `/app-download/${DevicePlatform.iOS}-${mappedImageLocale}.png`,
      },
      {
        mobileAppPromotionUrl: `/promotion/mobile-app-${mappedCmsLocale}`,
        image: getCanadaAndroidImage(locale),
      },
    ];
  }

  if (isDesktop && isROC) {
    return createDesktopReturn(mappedCmsLocale, mappedImageLocale, [
      DevicePlatform.iOS,
      DevicePlatform.Android,
    ]);
  }

  if (isDesktop && envIsNormal() && !isROC) {
    return createDesktopReturn(mappedCmsLocale, mappedImageLocale, [DevicePlatform.Android]);
  }

  //  Only Android and huawei supported for Mexico
  if (envIsMexico() && isMobile && (isIOS || isWinPhone)) {
    return [];
  }

  // For ENTIRE Canada only IOS and Android are supported
  if ((envIsCanada() || isROC) && isMobile && !isAndroid && !isIOS) {
    return [];
  }

  // For every other locale, android only is supported
  if (envIsNormal() && !['ca', 'ca-fr'].includes(locale) && isMobile && !isAndroid) {
    return [];
  }

  const platform = getDevicePlatform();

  if (envIsCanada() && platform === DevicePlatform.Android) {
    return [
      {
        mobileAppPromotionUrl: `/promotion/mobile-app-${mappedCmsLocale}`,
        image: getCanadaAndroidImage(locale),
      },
    ];
  }

  const image = `/app-download/${platform}-${mappedImageLocale}.png`;
  const mobileAppPromotionUrl = `/promotion/mobile-app-${mappedCmsLocale}`;
  return [{ mobileAppPromotionUrl, image }];
};

export default useGetMobileAppDownloadUrl;
